import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export type InstallationStatus =
  | 'requested' | 'assigned' | 'scheduled' | 'in_progress' | 'commissioned'
  | 'signed_off' | 'pending_handover' | 'handed_over' | 'closed' | 'cancelled' | 'on_hold';

export interface InstallationChecklistItem { id: number; title: string; status: string; remarks: string | null; sequence: number }
export interface InstallationPhoto { id: number; kind: string; fileName: string; fileUrl: string; lat: number | null; lng: number | null; capturedAt: string | null }
export interface InstallationHistory { id: number; fromStatus: string | null; toStatus: string; action: string; remarks: string | null; createdAt: string }
export interface PdiComment { id: number; comment: string; authorUserId: number | null; authorLabel: string | null; createdAt: string }

export interface Installation {
  id: number;
  requestCode: string;
  orderId: number | null;
  dispatchId: number | null;
  dealerId: number;
  clientId: number | null;
  siteId: number | null;
  productId: number | null;
  regionId: number | null;
  technicianId: number | null;
  vendorId: number | null;
  status: InstallationStatus;
  priority: string;
  scheduledDate: string | null;
  slaDueAt: string | null;
  slaBreached: boolean;
  delayDays: number;
  escalationLevel: number;
  materialReadyAt: string | null;
  commissioningRemarks: string | null;
  customerName: string | null;
  handoverCertificateUrl: string | null;
  remarks: string | null;
  createdAt: string;
  items?: Array<{ id: number; productId: number; quantity: number; serialNo: string | null }>;
  checklist?: InstallationChecklistItem[];
  photos?: InstallationPhoto[];
  history?: InstallationHistory[];
}

export interface CreateInstallationInput {
  dealerId: number;
  orderId?: number | null;
  dispatchId?: number | null;
  clientId?: number | null;
  siteId?: number | null;
  productId?: number | null;
  regionId?: number | null;
  priority?: string;
  remarks?: string;
  items?: { productId: number; quantity?: number; serialNo?: string }[];
}

export interface InstallationDashboard {
  total: number;
  byStatus: Record<string, number>;
  open: number;
  pending: number;
  ongoing: number;
  completed: number;
  inProgress: number;
  pendingHandover: number;
  closed: number;
  materialReady: number;
  slaBreached: number;
  slaPerformance: number;
  overdue: number;
  recent: Installation[];
}

const base = createCrudApi<Installation, CreateInstallationInput>('installations');

const post = async (id: number, path: string, body: Record<string, unknown> = {}): Promise<Installation> =>
  (await apiClient.post<ApiSuccess<Installation>>(`/installations/${id}/${path}`, body)).data.data;

export const installationsApi = {
  ...base,
  async dashboard(): Promise<InstallationDashboard> {
    return (await apiClient.get<ApiSuccess<InstallationDashboard>>('/installations/dashboard')).data.data;
  },
  markMaterialReady: (id: number, remarks?: string) => post(id, 'material-ready', { remarks }),
  listComments: async (id: number): Promise<PdiComment[]> => (await apiClient.get<ApiSuccess<PdiComment[]>>(`/installations/${id}/comments`)).data.data,
  addComment: async (id: number, comment: string): Promise<PdiComment> => (await apiClient.post<ApiSuccess<PdiComment>>(`/installations/${id}/comments`, { comment })).data.data,
  assign: (id: number, body: { technicianId?: number | null; vendorId?: number | null }) => post(id, 'assign', body),
  schedule: (id: number, body: { scheduledDate: string; slaDueAt?: string }) => post(id, 'schedule', body),
  start: (id: number) => post(id, 'start'),
  addChecklistItem: async (id: number, title: string) => (await apiClient.post<ApiSuccess<InstallationChecklistItem>>(`/installations/${id}/checklist`, { title })).data.data,
  updateChecklistItem: async (id: number, itemId: number, body: { status?: string; remarks?: string }) => (await apiClient.put<ApiSuccess<InstallationChecklistItem>>(`/installations/${id}/checklist/${itemId}`, body)).data.data,
  addPhoto: (id: number, body: { kind: string; fileName: string; fileUrl: string }) => post(id, 'photos', body),
  commission: (id: number, body: { remarks?: string; reportUrl?: string }) => post(id, 'commission', body),
  signoff: (id: number, body: { customerName: string; signatureUrl?: string; remarks?: string }) => post(id, 'signoff', body),
  requestHandover: (id: number) => post(id, 'handover'),
  approveHandover: (id: number, body: { certificateUrl?: string }) => post(id, 'handover/approve', body),
  rejectHandover: (id: number, reason: string) => post(id, 'handover/reject', { reason }),
  close: (id: number) => post(id, 'close'),
  cancel: (id: number, reason: string) => post(id, 'cancel', { reason }),
  escalate: (id: number, reason: string) => post(id, 'escalate', { reason }),
};
